import {
  useState,
  useMemo,
  useCallback,
  forwardRef,
  ForwardedRef,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
} from 'react'

import { clientLogError } from '@/src/lib/clientLog'

import type { CaptureOptionsBySource, ToggleSource } from '@livekit/components-core'
import { Track, TrackPublishOptions } from 'livekit-client'

import PopupInformation from '@/src/components/popup/PopupInformation'

import { useTrackToggle } from '@/src/hooks/room/track/useTrackToggle'
import { useNavigator } from '@/src/hooks/media/useNavigator'
import { useGetSourceIcon } from '@/src/hooks/useGetSourceIcon'

import Attention from '@/src/assets/icons/Attention'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

export interface TrackToggleProps<T extends ToggleSource>
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  source: T
  showIcon?: boolean
  initialState?: boolean
  onChange?: (enabled: boolean, isUserInitiated: boolean) => void
  captureOptions?: CaptureOptionsBySource<T>
  publishOptions?: TrackPublishOptions
  onDeviceError?: (error: Error) => void
  isPiP?: boolean
  skipWarmUp?: boolean
}

export const TrackToggle = forwardRef(function TrackToggle<T extends ToggleSource>(
  { showIcon, ...props }: TrackToggleProps<T>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const { primary_color, text_primary_color } = useAppSelector(selectBranding)
  const { cameraAccess, microphoneAccess, checkPermissionOnClick } = useNavigator()
  const { buttonProps, enabled } = useTrackToggle(props)

  const handlePopupClose = useCallback(() => setIsPopupOpen(false), [])

  const openPopup = useCallback(
    async (event) => {
      event.preventDefault()

      await checkPermissionOnClick()
        .then((access) => {
          if (props.source === Track.Source.Microphone) {
            if (!access.microphoneAccess) {
              setIsPopupOpen(true)
            } else {
              buttonProps?.onClick?.(event)
            }
          }

          if (props.source === Track.Source.Camera) {
            if (!access.cameraAccess) {
              setIsPopupOpen(true)
            } else {
              buttonProps?.onClick?.(event)
            }
          }
        })
        .catch((err) => {
          clientLogError(
            `Error with checkPermissionOnClick in openPopup function in TrackToggle: ${JSON.stringify(
              err,
            )}`,
          )
        })
    },
    [buttonProps, checkPermissionOnClick, props.source],
  )

  const renderIcon = useCallback(() => {
    // Строгая проверка на microphoneAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние microphoneAccess это null и проверка !microphoneAccess в таком случае не подойдет
    if (props.source === Track.Source.Microphone && microphoneAccess === false) {
      return <Attention className="lk-attention" onClick={openPopup} />
    }

    // Строгая проверка на cameraAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние cameraAccess это null и проверка !cameraAccess в таком случае не подойдет
    if (props.source === Track.Source.Camera && cameraAccess === false) {
      return <Attention className="lk-attention" onClick={openPopup} />
    }

    return null
  }, [cameraAccess, microphoneAccess, openPopup, props.source])

  const customBtnProps: DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > = useMemo(
    () => ({
      ...buttonProps,
      style: {
        ...buttonProps?.style,
        color: buttonProps?.style?.color ?? text_primary_color,
        backgroundColor:
          props.source === Track.Source.ScreenShare && enabled
            ? buttonProps?.style?.backgroundColor ?? primary_color
            : '',
        width:
          props.source === Track.Source.Microphone || props.source === Track.Source.Camera
            ? '100%'
            : '',
      },
      className: `${buttonProps.className || ''} ${
        props.source === Track.Source.Microphone || props.source === Track.Source.Camera
          ? 'lk-new-button'
          : ''
      }`.trim(),
    }),
    [buttonProps, props.source, enabled, primary_color, text_primary_color],
  )

  const Icon = useGetSourceIcon(props.source, enabled)

  return (
    <>
      {isPopupOpen && <PopupInformation onClose={handlePopupClose} />}

      {props.source === Track.Source.ScreenShare ? (
        <button ref={ref} {...customBtnProps}>
          {(showIcon ?? true) && Icon}
          {props.children}
        </button>
      ) : (
        <button ref={ref} {...customBtnProps} onClick={openPopup}>
          {renderIcon()}
          {(showIcon ?? true) && Icon}
          {props.children}
        </button>
      )}
    </>
  )
})

export default TrackToggle
